import * as React from 'react';
import {isServer, view, createViewConfig, assignProps} from '@sail/engine';
import {data} from 'currency-codes';
import type {View} from '../view';

import {CompositeField} from './CompositeField';
import type {CompositeFieldProps} from './CompositeField';
import {useSelection} from './hooks/useSelection';
import {TextField} from './TextField';
import {FormFieldConfig} from './FormField';
import {SelectField} from './SelectField';
import {PopoverConfig} from './popover';
import {css} from '../css';
import {useFormRegister} from './form/useFormRegister';

// TODO(jackl): data injection
// formatters and currencies get injected via a CurrencyContextProvider or something
// we fall back to Number.IntlFormat
// these can be overridden by props at individual call sites

// TODO (aleix, 2025-06-01): remove the hardcoded XCG and update currency-codes
// instead, which at this point should have already been updated to include the
// new currency.
const CURRENCIES = data.reduce(
  (acc, curr) => {
    acc[curr.code] = curr.currency;
    return acc;
  },
  {
    XCG: 'Caribbean Guilder',
  } as Record<string, string>,
);
const CURRENCY_CODES = Object.keys(CURRENCIES).sort();

const devWarn = (string: string) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(string);
  }
};

function matchesCurrency<T>(
  textValue: string,
  inputValue: string,
  item: T,
): boolean {
  const currencyName = CURRENCIES[(item as {id: string}).id];
  return (
    textValue.toLowerCase().includes(inputValue.toLowerCase()) ||
    currencyName?.toLowerCase().includes(inputValue.toLowerCase())
  );
}

export type CurrencyFieldProps = Omit<
  View.IntrinsicElement<'fieldset'>,
  'onChange'
> &
  Pick<
    CompositeFieldProps,
    'disabled' | 'description' | 'error' | 'hiddenElements'
  > & {
    /**
     * An **integer** representing the amount of the currency, denominated in that
     * **currency's smallest unit**. e.g. $10.00 is 1000 in USD (or any other two-decimal currency) and ¥100 is 100 in JPY (a zero-decimal currency)
     */
    value?: number | null;

    /**
     * The value the component should start with. This allows initial control
     * if the component is going to be uncontrolled
     */
    defaultValue?: number;

    /**
     * The amount the component should format and display as a placeholder when
     * no value is present.
     * If you pass in a number, we will format the number using the currency's
     * default number of fractional digits (e.g. 2 for USD), but if you pass a
     * string, we will use the literal value of the string in all situations.
     *
     * @default 0
     */
    placeholderValue: number | string;

    /**
     * While typing, by default, we will not display any fractional digits until
     * the user types a decimal point. Turn this to true if you want to always
     * show the fractional digits, even if they are all zeros.
     * When this is false, we still always show the fractional digits once the
     * user has blurred the field.
     *
     * @default false
     */
    showEmptyFractionDigits: boolean;

    /**
     * By default, we will show the fractional digits of the placeholder value
     * that are inherent to the selected currency. For example, if the placeholderValue
     * is set to `0`, we will display `0.00` as the placeholder. If you'd prefer
     * the older behavior where we hide unused fractional digits, you can set this
     * to false.
     * Note: this only applies when the type of `placeholderValue` is a number.
     * All formatting is bypassed when `placeholderValue` is a string.
     *
     * @default true
     */
    showEmptyPlaceholderFractionDigits: boolean;

    /**
     * The currency that the value is denominated in
     */
    currency?: string;

    /**
     * The currency the component should start with. This allows initial control
     * if the component is going to be uncontrolled
     */
    defaultCurrency?: string;

    /**
     * Override the allowed "sub-cent" accuracy. By default we will use the currency's default
     * number of fractional digits, e.g. 2 for USD. This allows you to override that
     * behavior, by providing a number of additional digits to allow. This should typically
     * only be used in rare cases, and may have unintended localization consequences. You must
     * make sure that the data source you are integrating with expects this, since it
     * often means that the we no longer provide a whole integer.
     * You can also provide a negative value to reduce the default fractional accuracy.
     */
    fractionDigitOffset: number;

    /**
     * If you use the `fractionDigitOffset` prop, by default, we will only pad the
     * fraction digits that are expected by default. In USD, this would be padding 2
     * zeros. ($5 -> $5.00). This is means that users aren't visually prompted to add
     * more decimals if they don't have them, but they will be allowed to add them.
     * If `padAllFractionDigits` is true, we will pad all the digits.
     * (e.g. $5 -> $5.0000)
     */
    padAllFractionDigits: boolean;

    /**
     * By default we allow users to type negative values into the field. Set this
     * to false to disable that behavior.
     *
     * @default true
     */
    allowNegative: boolean;

    /**
     * The list of selectable currencies
     */
    currencyAllowlist: string[];

    /**
     * The callback function to fire when the value changes
     */
    onChange?: (value: number | null) => void;

    /**
     * The callback function to fire when the currency changes
     */
    onCurrencyChange?: (currency: string) => void;

    /**
     * The locale the component will use when formatting values. If not
     * provided, will default to the locale from the user's browser
     */
    locale?: string;

    /**
     * Text that describes the control. Will be both visible and clickable
     */
    label?: React.ReactNode;

    /**
     * Label for the currency selection dropdown select element. Not typically user visible.
     */
    currencySelectLabel?: string;

    /**
     * The size of the field
     */
    size: 'small' | 'medium' | 'large';

    /**
     * Makes the field non-editable
     */
    readOnly?: boolean;

    /**
     * Controls whether the control should be automatically focused
     */
    autoFocus?: boolean;

    subviews?: View.Subviews<{
      /**
       * Customize the currency symbol node.
       */
      currencySymbol?: 'span';

      /**
       * Customize the value input node.
       */
      valueInput?: typeof TextField;
    }>;
  };

export const CurrencyFieldConfig = createViewConfig({
  props: {} as CurrencyFieldProps,
  name: 'CurrencyField',
  flattens: true,
  defaults: {
    allowNegative: true,
    currencyAllowlist: CURRENCY_CODES,
    disabled: false,
    fractionDigitOffset: 0,
    padAllFractionDigits: false,
    placeholderValue: 0,
    showEmptyFractionDigits: false,
    showEmptyPlaceholderFractionDigits: true,
    size: 'medium',
  },
});

type CurrencyMetaData = {
  symbol: string;
  symbolPosition: 'start' | 'end';
  denominationMultiple: number;
  fractionDigitMultiple: number;
  maximumFractionDigits: number;
  minimumFractionDigits: number;
  decimal: string;
  regEx: {
    nonNumericChars: RegExp;
    group: RegExp;
    decimal: RegExp;
    signSpace: RegExp;
  };
};

/**
 * @param autoFocus
 * @param defaultValue
 * @param disabled
 * @param form
 * @param maxLength
 * @param minLength
 * @param name
 * @param onChange
 * @param onKeyPress
 * @param onKeyDown
 * @param onKeyUp
 * @param placeholder
 * @param readOnly
 * @param required
 * @param tabIndex
 */
export const CurrencyField = CurrencyFieldConfig.createView((props) => {
  const {componentProps} = useFormRegister({props});

  const {
    allowNegative,
    autoFocus,
    currency,
    currencyAllowlist,
    currencySelectLabel = 'Currency',
    defaultCurrency,
    defaultValue,
    disabled,
    error,
    fractionDigitOffset,
    label,
    locale,
    name,
    onChange,
    onCurrencyChange,
    padAllFractionDigits,
    placeholderValue,
    readOnly,
    showEmptyFractionDigits,
    showEmptyPlaceholderFractionDigits,
    size,
    value,
    subviews,
    ...restProps
  } = componentProps as View.RenderProps<CurrencyFieldProps>;

  const valueIsControlledRef = React.useRef<boolean>(
    typeof value !== 'undefined',
  );
  const currencyIsControlledRef = React.useRef<boolean>(
    typeof currency !== 'undefined',
  );
  const compositeFieldRef = React.useRef<HTMLDivElement>(null);

  const internalLocale =
    locale ?? (isServer ? 'en-US' : navigator?.language ?? 'en-US');

  const doesSupportDecimals = fractionDigitOffset > 0;
  let initialInternalValue: number | null = null;

  // If we support decimals due to a positive fractionDigitOffset
  if (doesSupportDecimals) {
    // Set defaultValue or value as the initial value if they're numbers
    if (typeof defaultValue === 'number') {
      initialInternalValue = defaultValue;
    } else if (typeof value === 'number') {
      initialInternalValue = value;
    }
    // If we don't support decimals (common case)
  } else if (Number.isInteger(defaultValue)) {
    // If defaultValue is an integer, use it as the initial value
    initialInternalValue = defaultValue as number;
  } else if (Number.isInteger(value)) {
    // Otherwise if value is an integer, use it as the initial value
    initialInternalValue = value as number;
  }

  // Don't allow negative default or controlled values if allowNegative is false
  if (!allowNegative) {
    if (typeof defaultValue === 'number' && defaultValue < 0) {
      initialInternalValue = null;
      devWarn(
        'CurrencyField was provided a negative defaultValue while allowNegative was set to false. Ignoring defaultValue.',
      );
    } else if (typeof value === 'number' && value < 0) {
      initialInternalValue = null;
      devWarn(
        'CurrencyField was provided a negative value while allowNegative was set to false. Ignoring value.',
      );
    }
  }

  const [internalValue, setInternalValue] = React.useState<number | null>(
    initialInternalValue,
  );
  const [internalCurrency, setInternalCurrency] = React.useState<string>(
    defaultCurrency ?? currency ?? currencyAllowlist[0] ?? 'USD',
  );
  const [fractionalMode, setFractionalMode] = React.useState(false);
  // If we have ever displayed the digits from the fractionDigitOffset
  // it's jarring to take them away. So we track the state, and once we
  // show them, we always show them.
  const [hasShownFractionOffset, setHasShownFractionOffset] =
    React.useState<boolean>(padAllFractionDigits);
  const [internalSign, setInternalSign] = React.useState<1 | -1>(1);

  // parse a test value to back out currency metadata
  const {
    symbol,
    symbolPosition,
    denominationMultiple,
    fractionDigitMultiple,
    minimumFractionDigits,
    maximumFractionDigits,
    decimal,
    regEx,
  }: CurrencyMetaData = React.useMemo(() => {
    const testFormatter = Intl.NumberFormat(internalLocale, {
      style: 'currency',
      currency: internalCurrency,
    });
    const testForcedFractionFormatter = Intl.NumberFormat(internalLocale, {
      style: 'currency',
      currency: internalCurrency,
      minimumFractionDigits: 2,
    });

    const formattedParts = testFormatter.formatToParts(1234567890);
    const fractionalParts = testForcedFractionFormatter.formatToParts(5);

    const fractionalDecimalSymbol = fractionalParts.find(
      (part) => part.type === 'decimal',
    )?.value as string;

    const fractionDigits = formattedParts.find(
      (part) => part.type === 'fraction',
    )?.value;

    const minimumFractionDigits = fractionDigits ? fractionDigits.length : 0;

    const metadata = formattedParts.reduce(
      (acc, part) => ({
        ...acc,
        [part.type]: part.value,
      }),
      {} as Record<Intl.NumberFormatPartTypes, string>,
    );

    const {currency: symbol, decimal, group, fraction} = metadata;
    const computedDecimal =
      fractionDigitOffset > 0 ? fractionalDecimalSymbol : decimal;

    const fractionDigitMultiple: number =
      fractionDigitOffset > 0 ? 10 ** fractionDigitOffset : 1;

    const calculatedMaximumFractionDigits =
      (fraction?.length ?? 0) + fractionDigitOffset;
    return {
      symbol,
      symbolPosition: formattedParts[0].value === symbol ? 'start' : 'end',
      denominationMultiple: 1 * 10 ** (fraction?.length ?? 0),
      fractionDigitMultiple,
      minimumFractionDigits,
      maximumFractionDigits:
        calculatedMaximumFractionDigits >= 0
          ? calculatedMaximumFractionDigits
          : 0,
      decimal: computedDecimal,
      regEx: {
        nonNumericChars: new RegExp(`[^\\d\\${fractionalDecimalSymbol}]`, 'g'),
        group: new RegExp(`\\${group}`, 'g'),
        decimal: new RegExp(`\\${computedDecimal}`, 'g'),
        signSpace: /\u00A0/,
      },
    };
  }, [internalCurrency, internalLocale, fractionDigitOffset]);

  // set fractional mode to true if initial value isn't cleanly divisible
  React.useEffect(() => {
    if (
      typeof internalValue === 'number' &&
      internalValue % denominationMultiple !== 0
    ) {
      setFractionalMode(true);
    }
  }, [denominationMultiple, internalValue]);

  // sync value prop changes to state, with error handling
  React.useEffect(() => {
    if (value !== undefined) {
      if (valueIsControlledRef.current) {
        if (typeof value === 'number') {
          if (Number.isInteger(value) || fractionDigitOffset > 0) {
            const splitVal = `${value}`.split('.');
            const decimalLength =
              splitVal.length > 1
                ? splitVal[1].length
                : -splitVal[0]
                    .split('')
                    .reverse()
                    .findIndex((char) => char !== '0');

            if (decimalLength > fractionDigitOffset) {
              devWarn(
                `'value' must not have more decimal places than the fractionDigitOffset allows without accuracy loss. Max: ${fractionDigitOffset}, Actual: ${decimalLength} (${value})`,
              );
            }
            setInternalValue(value);
          } else {
            devWarn(
              '`value` must be an integer representing the smallest unit of the specified currency.',
            );
          }
        } else {
          setInternalValue(null);
        }
      } else {
        devWarn(
          'You attempted to set `value` after initializing `CurrencyField` as uncontrolled. This change will be ignored.',
        );
      }
    }
  }, [value, fractionDigitOffset]);

  // sync currency prop changes to state, with error handling
  React.useEffect(() => {
    if (typeof currency === 'string') {
      if (currencyIsControlledRef.current) {
        setInternalCurrency(currency);
      } else {
        devWarn(
          'You attempted to set `currency` after initializing `CurrencyField` as uncontrolled. This change will be ignored.',
        );
      }
    }
  }, [currency]);

  const valueInputRef = React.useRef<HTMLInputElement>(null);
  const {setSelection} = useSelection(valueInputRef);

  const formatter = React.useMemo(
    () =>
      Intl.NumberFormat(internalLocale, {
        style: 'currency',
        currency: internalCurrency,
        ...(fractionalMode || showEmptyFractionDigits
          ? {
              maximumFractionDigits,
              // If we opt to pad all fraction digits, or we have a currency
              // that doesn't usually have fractional digits, we will set
              // minimumFractionDigits to the same value as maximumFractionDigits
              minimumFractionDigits:
                hasShownFractionOffset || denominationMultiple === 1
                  ? maximumFractionDigits
                  : undefined,
            }
          : {
              // note: we may want a prop to control this in the future
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            }),
      }),
    [
      internalCurrency,
      internalLocale,
      fractionalMode,
      maximumFractionDigits,
      hasShownFractionOffset,
      denominationMultiple,
      showEmptyFractionDigits,
    ],
  );

  const placeholderFormatter = React.useMemo(
    () =>
      Intl.NumberFormat(internalLocale, {
        style: 'currency',
        currency: internalCurrency,
        ...(fractionalMode || showEmptyPlaceholderFractionDigits
          ? {
              maximumFractionDigits,
              // If we opt to pad all fraction digits, or we have a currency
              // that doesn't usually have fractional digits, we will set
              // minimumFractionDigits to the same value as maximumFractionDigits
              minimumFractionDigits:
                hasShownFractionOffset || denominationMultiple === 1
                  ? maximumFractionDigits
                  : undefined,
            }
          : {
              // note: we may want a prop to control this in the future
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            }),
      }),
    [
      internalCurrency,
      internalLocale,
      fractionalMode,
      maximumFractionDigits,
      hasShownFractionOffset,
      denominationMultiple,
      showEmptyPlaceholderFractionDigits,
    ],
  );

  const formatValue = React.useCallback(
    (value: number | null, type?: 'value' | 'placeholder'): string => {
      if (typeof value === 'number') {
        const valueToFormat = value / denominationMultiple;
        const selectedFormatter =
          type === 'placeholder' ? placeholderFormatter : formatter;
        return selectedFormatter
          .format(valueToFormat)
          .replace(symbol, '')
          .replace(regEx.signSpace, '')
          .trim();
      }
      return internalSign < 0 ? '-' : '';
    },
    [
      formatter,
      placeholderFormatter,
      symbol,
      regEx.signSpace,
      internalSign,
      denominationMultiple,
    ],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const {selectionStart: start, value: raw} = e.target;
    const newSign = allowNegative && raw.charAt(0) === '-' ? -1 : 1;
    let newValue = internalValue;

    // strip out everything but digits and decimal
    let value = raw.replace(regEx.nonNumericChars, '');

    // if the user started with a decimal character…
    if (value === decimal) {
      // ...handle that case specifically and return early
      setFractionalMode(true);
      setInternalValue(0);
      setSelection(2);
      return;
    }

    // count the characters removed before the cursor
    const removedChars = [
      ...raw.slice(0, start ?? 0).matchAll(regEx.nonNumericChars),
    ].length;

    // find the first decimal, if there is one
    const decimalIndex = value.indexOf(decimal);

    const hasDecimal = decimalIndex > -1;

    if (hasDecimal) {
      // remove any decimals after the first one
      if (decimalIndex !== value.lastIndexOf(decimal)) {
        value =
          value.slice(0, decimalIndex + 1) +
          value.slice(decimalIndex).replace(regEx.decimal, '');
      }

      // lop off any extraneous digits to prevent rounding later
      // add 1 because slice is inclusive -> exclusive
      value = value.slice(0, decimalIndex + maximumFractionDigits + 1);
    }

    const valueParts = value.split(decimal);
    const decimalDigits = valueParts.length > 1 ? valueParts[1] : '';

    // For speed and readability
    // This is the number of digits after the decimal point as all zeros
    // e.g. $2.00 => '00'
    let placeholderZeros = '';
    for (let i = 0; i < maximumFractionDigits; i++) {
      placeholderZeros += '0';
    }

    // We want to determine if we have more decimal digits
    // than natively occurs in this currency and if our cursor is
    // at the end rather than typing through pre-padded zeros
    if (
      decimalDigits.length > minimumFractionDigits &&
      (valueInputRef.current as HTMLInputElement)?.selectionEnd === value.length
    ) {
      setHasShownFractionOffset(true);
    }

    if (value.length === 0 || value === `${decimal}${placeholderZeros}`) {
      // if we deleted everything relevant, reset
      newValue = null;
      setFractionalMode(false);
      setInternalSign(1);
    } else {
      // convert to US-style decimal separators so JS can parse it. USA #1?
      newValue = Number(value.replace(regEx.decimal, '.'));

      if (!hasDecimal && fractionalMode) {
        // if the decimal was removed...
        newValue /= fractionDigitMultiple;
        if (Math.abs(newValue) === Math.abs(internalValue ?? 0)) {
          // ...and that was all the user did, divide by the denomination multiple to maintain identity
          newValue /= denominationMultiple;

          if (Number.isInteger(newValue)) {
            // if the resulting value is an integer, set fractional mode false
            // if it isn't, no-op, we need to keep the fractions around
            setFractionalMode(false);
          }
        } else {
          // ...and the user deleted other digits, leave the value alone and set fractional mode false
          setFractionalMode(false);
        }
      } else if (hasDecimal && !fractionalMode) {
        // if the decimal was added, set fractional mode true
        setFractionalMode(true);
      }

      // multiply by the denomination multiple to store the smallest unit, then add the sign
      newValue *= denominationMultiple * newSign;

      // Round b/c JS is weird
      // but take into account the fractionDigitOffset
      // in situations when the offset is positive (showing more decimals)
      // TODO: This reduces the maximum value that can be entered since we're making
      // the number larger by multiplying by the fractionDigitMultiple safely
      // Theoretically we could use a bignum implementation, or we could treat
      // the left an right sides of the decimal as their own independent numbers
      newValue =
        Math.round((newValue + Number.EPSILON) * fractionDigitMultiple) /
        fractionDigitMultiple;
    }

    if (!Number.isNaN(newValue) && newValue !== internalValue) {
      setInternalValue(newValue);
      onChange?.(newValue);
    }

    if (newSign !== internalSign) {
      setInternalSign(newSign);
    }

    if (start !== null) {
      // count the group separators before the cursor in the new value
      const currentGroupSeparators = [
        ...formatValue(newValue)
          .slice(0, start ?? 0)
          .matchAll(regEx.group),
      ].length;

      // set the selection start...
      setSelection(
        start +
          // adjust for any separators that appeared
          currentGroupSeparators -
          // adjust for removed characters
          removedChars +
          // adjust for negative number formatting
          (newSign < 0 ? 1 : 0),
      );
    }
  };

  const handleBlur: typeof restProps.onBlur = (e) => {
    if (typeof internalValue === 'number' && !(disabled || readOnly)) {
      setFractionalMode(true);
    }
    if (restProps.onBlur) {
      restProps.onBlur(e);
    }
  };

  const handleSelectChange = (key: React.Key): void => {
    setInternalCurrency(key as string);
    onCurrencyChange?.(key as string);
  };

  const symbolSpan = (
    <view.span
      // TODO: SAIL-3215 no hardcoded strings
      aria-label="Currency symbol"
      css={{width: 'fit'}}
      inherits={subviews.currencySymbol}
    >
      {symbol}
    </view.span>
  );

  const currencyData = React.useMemo(() => {
    const items = [];
    const disabledItems = [];
    let isSelectedCurrencyInAllowlist = false;

    for (let i = 0; i < currencyAllowlist.length; i++) {
      const currency = currencyAllowlist[i];

      items.push({
        id: currency,
        label: currency,
      });

      isSelectedCurrencyInAllowlist =
        isSelectedCurrencyInAllowlist || currency === internalCurrency;
    }

    // If the selected country is not in the allowlist, add it as a disabled option at the top of the list
    if (!isSelectedCurrencyInAllowlist) {
      items.unshift({
        id: internalCurrency,
        label: internalCurrency,
      });
      disabledItems.push(internalCurrency);
    }

    return {items, disabledItems};
  }, [currencyAllowlist, internalCurrency]);

  const shouldShowCurrencySelector =
    currencyAllowlist?.length >
    (!currencyAllowlist.includes(internalCurrency) ? 0 : 1);

  const currencySelector = shouldShowCurrencySelector ? (
    <SelectField
      onSelectionChange={handleSelectChange}
      selectedKey={internalCurrency}
      disabledKeys={currencyData.disabledItems}
      readOnly={readOnly}
      disabled={disabled}
      filterable
      label={currencySelectLabel}
      css={{minWidth: 'fit'}}
      size={size}
      subviews={{
        button: {
          css: [
            css({
              border: 'none',
              keyline: 'none',
              topShadow: 'none',
            }),
            error
              ? css({
                  ':hover:not(:active):not([aria-disabled="true"])': {
                    keyline: 'form.error',
                  },
                })
              : css({}),
            symbolPosition !== 'end'
              ? css({
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                })
              : css({
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                }),
          ],
        },
      }}
      uses={[
        PopoverConfig.customize({
          uses: [assignProps({targetRef: compositeFieldRef})],
        }),
      ]}
      data={currencyData.items}
      item={{
        textValue: (item) => (item as {label: string}).label,
        description: (item) => CURRENCIES[(item as {id: string}).id],
      }}
      defaultFilter={matchesCurrency}
    />
  ) : null;

  const inputPaddingReset =
    symbolPosition !== 'end'
      ? css({
          paddingTop: 'space.0',
          paddingBottom: 'space.0',
          paddingRight: 'space.0',
        })
      : css({
          paddingTop: 'space.0',
          paddingBottom: 'space.0',
          paddingLeft: 'space.0',
        });

  return (
    <CompositeField
      {...restProps}
      inherits={props.inherits}
      label={label}
      disabled={disabled}
      size={size}
      onBlur={handleBlur}
      error={error}
      subviews={{
        inputs: {
          uses: [
            assignProps({
              ref: compositeFieldRef,
            }),
          ],
          css: shouldShowCurrencySelector ? inputPaddingReset : undefined,
        },
      }}
    >
      {symbolPosition === 'start' ? symbolSpan : currencySelector}
      <FormFieldConfig.Provider value={{css: {width: 'fill'}}}>
        <TextField
          ref={valueInputRef}
          inherits={subviews.valueInput}
          {...(symbolPosition === 'end' ? {css: {marginLeft: 'xsmall'}} : {})}
          onChange={handleChange}
          // error={error}
          placeholder={
            typeof placeholderValue === 'string'
              ? placeholderValue
              : formatValue(placeholderValue, 'placeholder')
          }
          inputMode={denominationMultiple === 1 ? 'numeric' : 'decimal'}
          value={formatValue(internalValue)}
          readOnly={readOnly}
          autoFocus={autoFocus}
          label="Value"
        />
      </FormFieldConfig.Provider>
      {symbolPosition === 'end' ? symbolSpan : currencySelector}
      <view.input
        type="hidden"
        name={name}
        value={internalValue ?? ''}
        disabled={disabled}
      />
    </CompositeField>
  );
});
